// src/components/admin/EmbeddingHealthTile.js
// Admin-only, read-only monitor for missing vector embeddings.
// The ingestion pipeline treats embed-chunks failures as non-fatal, so a
// document can be fully searchable by keyword while silently missing its
// vector embeddings. This tile makes that gap visible.
import React, { useCallback, useEffect, useState } from 'react';
import { Card, MaterialIcon, StatusPill } from '../ui';
import { documentsService } from '../../services/documentsService';

const STATUS_VARIANT = {
  live: 'active',
  validating: 'pending',
  failed: 'overdue',
};

const EmbeddingHealthTile = ({ hoaId }) => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await documentsService.getEmbeddingHealth(hoaId);
    setHealth(result);
    setLoading(false);
  }, [hoaId]);

  useEffect(() => {
    load();
  }, [load]);

  const hasError = Boolean(health?.error);
  const missing = health?.missingChunks || 0;
  const flaggedDocs = (health?.documents || []).filter(d => !d.processing);
  const processingDocs = (health?.documents || []).filter(d => d.processing);
  const healthy = !loading && !hasError && missing === 0;

  let icon = 'hub';
  let iconClass = 'text-secondary';
  if (!loading && hasError) { icon = 'error'; iconClass = 'text-red-700'; }
  else if (!loading && missing > 0) { icon = 'warning'; iconClass = 'text-amber-700'; }
  else if (healthy) { icon = 'check_circle'; iconClass = 'text-green-700'; }

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <div className="w-12 h-12 bg-section-bg rounded-xl flex items-center justify-center shrink-0">
            <MaterialIcon name={icon} size={24} className={iconClass} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">AI Search Health</p>
            <h3 className="text-lg font-bold text-primary-dark">Vector embeddings</h3>
            {loading && (
              <p className="text-sm font-medium text-slate-500">Checking document chunks…</p>
            )}
            {!loading && hasError && (
              <p className="text-sm font-medium text-red-700">Could not run the check: {health.error}</p>
            )}
            {!loading && !hasError && healthy && (
              <p className="text-sm font-medium text-slate-500">
                All {health.activeChunks.toLocaleString()} active chunks are embedded. Search is using full hybrid (vector + keyword) retrieval.
              </p>
            )}
            {!loading && !hasError && missing > 0 && (
              <p className="text-sm font-medium text-primary-dark opacity-90">
                <span className="text-2xl font-extrabold text-secondary mr-1">{missing.toLocaleString()}</span>
                of {health.activeChunks.toLocaleString()} active chunks have no embedding
                {health.truncated ? ' (showing first 1,000)' : ''}. These documents are searchable by keyword only.
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="border border-secondary text-secondary px-4 py-2 rounded-full text-sm font-bold hover:bg-secondary hover:text-white transition-all disabled:opacity-50 inline-flex items-center gap-1 shrink-0"
        >
          <MaterialIcon name="refresh" size={18} />
          Recheck
        </button>
      </div>

      {!loading && !hasError && flaggedDocs.length > 0 && (
        <div className="mt-5 space-y-2">
          {flaggedDocs.map(doc => (
            <div key={doc.documentId} className="flex items-center justify-between gap-3 bg-section-bg rounded-xl px-4 py-2.5">
              <span className="text-sm font-semibold text-primary-dark truncate">{doc.title}</span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] font-bold uppercase tracking-tighter text-slate-500">
                  {doc.missing} chunk{doc.missing !== 1 ? 's' : ''}
                </span>
                <StatusPill label={doc.aiStatus} variant={STATUS_VARIANT[doc.aiStatus] || 'info'} />
              </div>
            </div>
          ))}
          <p className="text-xs font-medium text-slate-500 pt-2 leading-relaxed">
            Likely cause: the embed-chunks Edge Function failed (check that VOYAGE_API_KEY is set and valid in Supabase secrets, then review the function logs).
          </p>
        </div>
      )}

      {!loading && !hasError && processingDocs.length > 0 && (
        <p className="mt-4 text-xs font-medium text-slate-500">
          {processingDocs.length} recently uploaded document{processingDocs.length !== 1 ? 's are' : ' is'} still being embedded.
        </p>
      )}
    </Card>
  );
};

export default EmbeddingHealthTile;
